// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import { useHomeMetricsQuery } from "@/api/hooks/use-home";
import { Skeleton } from "@/components/ui/skeleton";

// Digit grouping is a regional setting, like date order — so the host locale, not the UI language.
const COUNT = new Intl.NumberFormat(undefined);

function Metric({ label, value, isLoading }: { label: string; value: number | undefined; isLoading: boolean }) {
  return (
    <div className="space-y-1 px-6 first:pl-0">
      <p className="text-muted-foreground text-sm">{label}</p>
      {isLoading ? (
        <Skeleton className="h-9 w-24" />
      ) : (
        <p className="font-semibold text-3xl tabular-nums tracking-tight">
          {value === undefined ? "—" : COUNT.format(value)}
        </p>
      )}
    </div>
  );
}

/** Headline counts: active stores, devices and users, and today's expected images. */
export function HomeMetrics() {
  const { t } = useTranslation("back-office");
  const { data, isLoading } = useHomeMetricsQuery();

  return (
    <div className="flex flex-wrap divide-x">
      <Metric label={t("home.metrics.stores")} value={data?.activeStores} isLoading={isLoading} />
      <Metric label={t("home.metrics.devices")} value={data?.activeDevices} isLoading={isLoading} />
      <Metric label={t("home.metrics.users")} value={data?.activeUsers} isLoading={isLoading} />
      <Metric label={t("home.metrics.expectedImages")} value={data?.expectedImages} isLoading={isLoading} />
    </div>
  );
}
